// Airports.jsx - Airport Browser page
// Searchable list of all 4,878 airports with filtering

import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useWorldState } from "../components/state/WorldStateContext.jsx";
import { GLOBAL_AIRPORTS } from "../data/GlobalAirports.js";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
// Icons removed - text-only UI

// Hub tier config
const TIER_CONFIG = {
  major: { label: "Major Hub", color: "#f59e0b", bgColor: "#78350f" },
  large: { label: "Large Hub", color: "#3b82f6", bgColor: "#1e3a5f" },
  medium: { label: "Medium", color: "#22c55e", bgColor: "#052e16" },
  small: { label: "Small", color: "#737373", bgColor: "#262626" },
};

// Region/continent config
const REGIONS = {
  NA: "North America",
  SA: "South America",
  EU: "Europe",
  AF: "Africa",
  AS: "Asia",
  OC: "Oceania",
};

// Calculate distance between two airports
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 3440.065; // nm
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
}

// Format distance
function formatDistance(nm) {
  if (nm >= 1000) return `${(nm / 1000).toFixed(1)}K nm`;
  return `${nm} nm`;
}

// Airport Card Component
function AirportCard({ airport, hubAirport, playerSlots, onView, onAddRoute }) {
  const tierConfig = TIER_CONFIG[airport.hub_tier] || TIER_CONFIG.small;
  const distance = hubAirport
    ? calculateDistance(hubAirport.lat, hubAirport.lon, airport.lat, airport.lon)
    : 0;
  const slotsOwned = playerSlots[airport.icao]?.length || 0;

  return (
    <div
      className="p-4 rounded-lg hover:bg-zinc-800/50 transition-colors"
      style={{ backgroundColor: "#0c0c0c", border: "1px solid #1a1a1a" }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* ICAO and name */}
          <div className="flex items-center gap-3 mb-1">
            <span className="font-mono text-lg font-bold" style={{ color: "#60ffd0" }}>
              {airport.icao}
            </span>
            {airport.iata && (
              <span className="text-xs px-1 rounded" style={{ backgroundColor: "#262626", color: "#a1a1a1" }}>
                {airport.iata}
              </span>
            )}
            <Badge
              style={{
                backgroundColor: tierConfig.bgColor,
                color: tierConfig.color,
                border: `1px solid ${tierConfig.color}`,
              }}
            >
              {tierConfig.label}
            </Badge>
          </div>

          {/* Name and city */}
          <div className="text-sm mb-2" style={{ color: "#e5e5e5" }}>
            {airport.name}
          </div>
          <div className="text-sm" style={{ color: "#737373" }}>
            {airport.city}
            {airport.country && airport.country !== "US" && `, ${airport.country}`}
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-4 mt-3 text-xs">
            {hubAirport && (
              <div style={{ color: "#a1a1a1" }}>
                {formatDistance(distance)} from hub
              </div>
            )}
            <div style={{ color: slotsOwned > 0 ? "#22c55e" : "#737373" }}>
              {slotsOwned > 0 ? `${slotsOwned} slots owned` : "No slots"}
            </div>
            {airport.demand && (
              <div style={{ color: "#a1a1a1" }}>
                {airport.demand}% demand
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 ml-4">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onView(airport)}
            style={{ borderColor: "#404040", color: "#a1a1a1" }}
          >
            View
          </Button>
          <Button
            size="sm"
            onClick={() => onAddRoute(airport)}
            style={{ backgroundColor: "#22c55e", color: "#000" }}
          >
            + Route
          </Button>
        </div>
      </div>
    </div>
  );
}

// Airport Detail Modal
function AirportDetailModal({ airport, hubAirport, playerSlots, playerRoutes, onClose, onAddRoute }) {
  if (!airport) return null;

  const tierConfig = TIER_CONFIG[airport.hub_tier] || TIER_CONFIG.small;
  const distance = hubAirport
    ? calculateDistance(hubAirport.lat, hubAirport.lon, airport.lat, airport.lon)
    : 0;
  const slotsOwned = playerSlots[airport.icao]?.length || 0;

  // Find routes to/from this airport
  const relatedRoutes = playerRoutes.filter(
    (r) => r.origin === airport.icao || r.destination === airport.icao
  );

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div
        className="bg-zinc-900 rounded-lg max-w-xl w-full mx-4 max-h-[80vh] overflow-y-auto"
        style={{ border: "1px solid #333" }}
      >
        {/* Header */}
        <div
          className="sticky top-0 flex items-center justify-between p-4 border-b"
          style={{ backgroundColor: "#0a0a0a", borderColor: "#333" }}
        >
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-2xl font-bold" style={{ color: "#60ffd0" }}>
                {airport.icao}
              </span>
              {airport.iata && (
                <span className="text-sm px-2 py-1 rounded" style={{ backgroundColor: "#262626", color: "#a1a1a1" }}>
                  {airport.iata}
                </span>
              )}
            </div>
            <div className="text-sm mt-1" style={{ color: "#a1a1a1" }}>
              {airport.name}
            </div>
          </div>
          <button onClick={onClose} style={{ color: "#737373", fontSize: "1.5rem" }}>
            X
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Location */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-bold" style={{ color: "#fff" }}>Location</span>
            </div>
            <div className="text-sm" style={{ color: "#a1a1a1" }}>
              {airport.city}, {airport.country}
              {airport.continent && ` (${REGIONS[airport.continent] || airport.continent})`}
            </div>
          </div>

          {/* Hub Tier */}
          <div>
            <Badge
              style={{
                backgroundColor: tierConfig.bgColor,
                color: tierConfig.color,
                border: `1px solid ${tierConfig.color}`,
              }}
            >
              {tierConfig.label}
            </Badge>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded" style={{ backgroundColor: "#0c0c0c", border: "1px solid #1a1a1a" }}>
              <div className="text-xs uppercase mb-1" style={{ color: "#525252" }}>
                Distance from Hub
              </div>
              <div className="text-lg font-bold" style={{ color: "#60ffd0" }}>
                {formatDistance(distance)}
              </div>
            </div>
            <div className="p-3 rounded" style={{ backgroundColor: "#0c0c0c", border: "1px solid #1a1a1a" }}>
              <div className="text-xs uppercase mb-1" style={{ color: "#525252" }}>
                Your Slots
              </div>
              <div
                className="text-lg font-bold"
                style={{ color: slotsOwned > 0 ? "#22c55e" : "#737373" }}
              >
                {slotsOwned > 0 ? slotsOwned : "None"}
              </div>
            </div>
            <div className="p-3 rounded" style={{ backgroundColor: "#0c0c0c", border: "1px solid #1a1a1a" }}>
              <div className="text-xs uppercase mb-1" style={{ color: "#525252" }}>
                Demand
              </div>
              <div className="text-lg font-bold" style={{ color: "#fff" }}>
                {airport.demand || 50}%
              </div>
            </div>
            <div className="p-3 rounded" style={{ backgroundColor: "#0c0c0c", border: "1px solid #1a1a1a" }}>
              <div className="text-xs uppercase mb-1" style={{ color: "#525252" }}>
                Landing Fee
              </div>
              <div className="text-lg font-bold" style={{ color: "#f59e0b" }}>
                ${((airport.landing_fee || 5000) / 1000).toFixed(0)}K
              </div>
            </div>
          </div>

          {/* Related Routes */}
          {relatedRoutes.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-bold" style={{ color: "#fff" }}>Your Routes</span>
              </div>
              <div className="space-y-2">
                {relatedRoutes.map((route) => (
                  <div
                    key={route.id}
                    className="p-2 rounded text-sm"
                    style={{ backgroundColor: "#0c0c0c", border: "1px solid #1a1a1a" }}
                  >
                    <span style={{ color: "#60ffd0" }}>{route.origin}</span>
                    <span style={{ color: "#525252" }}> - </span>
                    <span style={{ color: "#60ffd0" }}>{route.destination}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Coordinates (for reference) */}
          <div className="text-xs" style={{ color: "#525252" }}>
            Coordinates: {airport.lat?.toFixed(4)}, {airport.lon?.toFixed(4)} | Elevation:{" "}
            {airport.elevation || 0} ft
          </div>
        </div>

        {/* Footer */}
        <div
          className="sticky bottom-0 flex items-center justify-end gap-3 p-4 border-t"
          style={{ backgroundColor: "#0a0a0a", borderColor: "#333" }}
        >
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={() => {
              onAddRoute(airport);
              onClose();
            }}
            style={{ backgroundColor: "#22c55e", color: "#000" }}
          >
            + Create Route
          </Button>
        </div>
      </div>
    </div>
  );
}

// Main Airport Browser Component
export default function AirportsPage() {
  const navigate = useNavigate();
  const { worldState } = useWorldState();

  const hub = worldState?.airline?.hub || "KPDX";
  const hubAirport = GLOBAL_AIRPORTS[hub];
  const playerSlots = worldState?.slots || {};
  const playerRoutes = worldState?.routes || [];

  // Search and filter state
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // Modal state
  const [selectedAirport, setSelectedAirport] = useState(null);

  // Pagination for virtual scrolling
  const [displayCount, setDisplayCount] = useState(50);
  const listRef = useRef(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 200);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(50);
  }, [debouncedSearch, tierFilter, regionFilter]);

  // Convert airports to array and filter
  const filteredAirports = useMemo(() => {
    const airportList = Object.entries(GLOBAL_AIRPORTS).map(([code, data]) => ({
      icao: code,
      ...data,
    }));

    let filtered = airportList;

    // Apply search filter
    if (debouncedSearch.trim()) {
      const s = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.icao?.toLowerCase().includes(s) ||
          a.iata?.toLowerCase().includes(s) ||
          a.name?.toLowerCase().includes(s) ||
          a.city?.toLowerCase().includes(s) ||
          a.country?.toLowerCase().includes(s)
      );
    }

    // Apply tier filter
    if (tierFilter !== "all") {
      filtered = filtered.filter((a) => a.hub_tier === tierFilter);
    }

    // Apply region filter
    if (regionFilter !== "all") {
      filtered = filtered.filter((a) => a.continent === regionFilter);
    }

    // Sort by distance from hub, then by tier
    if (hubAirport) {
      filtered.sort((a, b) => {
        // Put player's hub first
        if (a.icao === hub) return -1;
        if (b.icao === hub) return 1;

        // Then by tier
        const tierOrder = { major: 0, large: 1, medium: 2, small: 3 };
        const tierDiff = (tierOrder[a.hub_tier] || 3) - (tierOrder[b.hub_tier] || 3);
        if (tierDiff !== 0) return tierDiff;

        // Then by distance
        const distA = calculateDistance(hubAirport.lat, hubAirport.lon, a.lat, a.lon);
        const distB = calculateDistance(hubAirport.lat, hubAirport.lon, b.lat, b.lon);
        return distA - distB;
      });
    }

    return filtered;
  }, [debouncedSearch, tierFilter, regionFilter, hubAirport, hub]);

  // Visible airports (pagination)
  const visibleAirports = filteredAirports.slice(0, displayCount);

  // Load more on scroll
  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      setDisplayCount((prev) => Math.min(prev + 50, filteredAirports.length));
    }
  }, [filteredAirports.length]);

  // Handle add route
  const handleAddRoute = useCallback(
    (airport) => {
      // Navigate to routes page with destination pre-selected
      navigate("/routes", { state: { newRouteDestination: airport.icao } });
    },
    [navigate]
  );

  // Hub airport card (always shown at top)
  const HubCard = hubAirport && (
    <div
      className="p-4 rounded-lg"
      style={{
        backgroundColor: "#0d1f1a",
        border: "2px solid #60ffd0",
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-bold uppercase" style={{ color: "#60ffd0" }}>
          ★ Your Hub
        </span>
      </div>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="font-mono text-xl font-bold" style={{ color: "#60ffd0" }}>
              {hub}
            </span>
            {hubAirport.iata && (
              <span className="text-xs px-1 rounded" style={{ backgroundColor: "#262626", color: "#a1a1a1" }}>
                {hubAirport.iata}
              </span>
            )}
          </div>
          <div className="text-sm" style={{ color: "#e5e5e5" }}>
            {hubAirport.name}
          </div>
          <div className="text-sm" style={{ color: "#737373" }}>
            {hubAirport.city}, {hubAirport.country}
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs">
            <span style={{ color: "#22c55e" }}>
              {playerSlots[hub]?.length || 0} slots
            </span>
            <span style={{ color: "#a1a1a1" }}>
              {playerRoutes.filter((r) => r.origin === hub || r.destination === hub).length} routes
            </span>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setSelectedAirport({ icao: hub, ...hubAirport })}
          style={{ borderColor: "#60ffd0", color: "#60ffd0" }}
        >
          Details
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#000" }}>
      {/* Header */}
      <div
        className="sticky top-0 z-40 flex items-center justify-between p-4"
        style={{ backgroundColor: "#0a0a0a", borderBottom: "1px solid #1a1a1a" }}
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/opscenter")}
            style={{ color: "#a1a1a1" }}
          >
            &lt; Back
          </Button>
          <div>
            <h1 className="text-xl font-bold" style={{ color: "#fff" }}>
              Airports
            </h1>
            <p className="text-xs" style={{ color: "#737373" }}>
              {filteredAirports.length.toLocaleString()} airports available
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 flex-1 max-w-xl mx-8">
          <div className="relative flex-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search ICAO, IATA, city, or name..."
              className="w-full px-4 py-2 rounded-lg bg-zinc-900 text-white"
              style={{ border: "1px solid #333" }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: "#525252" }}
              >
                X
              </button>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            style={{
              borderColor: showFilters ? "#60ffd0" : "#333",
              color: showFilters ? "#60ffd0" : "#a1a1a1",
            }}
          >
            Filter
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div
          className="flex items-center gap-4 px-4 py-3"
          style={{ backgroundColor: "#0c0c0c", borderBottom: "1px solid #1a1a1a" }}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm" style={{ color: "#737373" }}>
              Tier:
            </span>
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="px-3 py-1 rounded bg-zinc-900 text-white text-sm"
              style={{ border: "1px solid #333" }}
            >
              <option value="all">All Tiers</option>
              <option value="major">Major Hubs</option>
              <option value="large">Large Hubs</option>
              <option value="medium">Medium</option>
              <option value="small">Small</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm" style={{ color: "#737373" }}>
              Region:
            </span>
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="px-3 py-1 rounded bg-zinc-900 text-white text-sm"
              style={{ border: "1px solid #333" }}
            >
              <option value="all">All Regions</option>
              {Object.entries(REGIONS).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          {(tierFilter !== "all" || regionFilter !== "all") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setTierFilter("all");
                setRegionFilter("all");
              }}
              style={{ color: "#ef4444" }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      )}

      {/* Main content */}
      <div
        ref={listRef}
        onScroll={handleScroll}
        className="p-4 space-y-4"
        style={{ maxHeight: "calc(100vh - 140px)", overflowY: "auto" }}
      >
        {/* Hub card */}
        {HubCard}

        {/* Section header */}
        <div className="flex items-center justify-between pt-4">
          <h2 className="text-sm font-bold uppercase" style={{ color: "#737373" }}>
            Available Airports ({filteredAirports.length.toLocaleString()})
          </h2>
        </div>

        {/* Airport list */}
        {filteredAirports.length === 0 ? (
          <div className="text-center py-12" style={{ color: "#737373" }}>
            <p>No airports match your search.</p>
            <p className="text-sm mt-2">Try a different search term or filter.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {visibleAirports.map((airport) => (
              <AirportCard
                key={airport.icao}
                airport={airport}
                hubAirport={hubAirport}
                playerSlots={playerSlots}
                onView={setSelectedAirport}
                onAddRoute={handleAddRoute}
              />
            ))}
            {displayCount < filteredAirports.length && (
              <div className="text-center py-4" style={{ color: "#525252" }}>
                Scroll to load more... ({displayCount} of {filteredAirports.length})
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedAirport && (
        <AirportDetailModal
          airport={selectedAirport}
          hubAirport={hubAirport}
          playerSlots={playerSlots}
          playerRoutes={playerRoutes}
          onClose={() => setSelectedAirport(null)}
          onAddRoute={handleAddRoute}
        />
      )}
    </div>
  );
}
